import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ViewConfig } from "@lib/views";
import {
    AccrualMatchColumn,
    AccrualMatchSortValue,
    MatchableItem,
    MatchItemType,
} from "../types";
import { createAccrualMatchViewConfig } from "../accrualMatchViewConfig";

type AllocationViewConfig = ViewConfig<
    MatchableItem,
    AccrualMatchColumn,
    AccrualMatchSortValue
>;

const CASH_LEDGER_TITLE = "Cash ledger";
const ACCRUAL_LEDGER_TITLE = "Accrual ledger";

// The shared date column is a fixed 120px. Here it also hosts the expand
// chevron, which clips "MMM d, yyyy" at that width.
const ALLOCATION_DATE_COLUMN_SIZE = {
    minSize: 160,
    maxSize: 160,
};

export function createAllocationViewConfig(
    matchItemType: MatchItemType | null,
): AllocationViewConfig {
    const config = createAccrualMatchViewConfig(matchItemType, {
        ledgerBasis: AccountingLedgerBasis.accruals,
    });

    return {
        // Editing allocations is an accrual-only flow (line-item allocation across
        // invoices/bills), so the GL column is always the accrual ledger.
        // Invoice and bill matches use the same account titles as the
        // find-match step.
        ...config,
        columns: {
            ...config.columns,
            [AccrualMatchColumn.DATE]: {
                ...config.columns[AccrualMatchColumn.DATE],
                ...ALLOCATION_DATE_COLUMN_SIZE,
            },
        },
        nesting: {
            enabled: true,
            expandColumn: AccrualMatchColumn.DATE,
            childrenKey: "children",
        },
    };
}

function withStandardLedgerTitles(
    config: AllocationViewConfig,
): AllocationViewConfig {
    return {
        ...config,
        columns: {
            ...config.columns,
            [AccrualMatchColumn.CASH_LEDGER]: {
                ...config.columns[AccrualMatchColumn.CASH_LEDGER],
                name: CASH_LEDGER_TITLE,
                header: CASH_LEDGER_TITLE,
            },
            [AccrualMatchColumn.ACCRUAL_LEDGER]: {
                ...config.columns[AccrualMatchColumn.ACCRUAL_LEDGER],
                name: ACCRUAL_LEDGER_TITLE,
                header: ACCRUAL_LEDGER_TITLE,
            },
        },
    };
}

export function createResolveDifferenceViewConfig(
    matchItemType: MatchItemType | null,
): AllocationViewConfig {
    return withStandardLedgerTitles(createAllocationViewConfig(matchItemType));
}
